
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Breadcrumbs,
  Chip,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdLocalOffer, MdSchedule } from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { BiPlay } from "react-icons/bi";
import { fetchDataFromApi } from "../../utils/api";
import { MyContext } from "../../App";
import { useNavigate } from "react-router-dom";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const ActiveFestiveOffers = () => {
  const [activeOffers, setActiveOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const context = useContext(MyContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveOffers();
  }, []);

  // Helper function to check if URL is video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return videoExtensions.includes(extension);
  };

  const fetchActiveOffers = async () => {
    setLoading(true);
    try {
      const response = await fetchDataFromApi("/api/homeFestiveBanner/active");
      if (response) {
        setActiveOffers(response);
      }
    } catch (error) {
      console.error("Error fetching active offers:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to fetch active offers",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return "No end date";
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return "Expired";
    if (days === 0) return "Expires today";
    return `${days} days remaining`;
  };

  const getTimeProgress = (startDate, endDate) => {
    if (!endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    const total = end - start;
    const elapsed = now - start;
    return Math.min(Math.max((elapsed / total) * 100, 0), 100);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="right-content w-100">
      <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
        <h5 className="mb-0">Active Offers</h5>

        <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
          <StyledBreadcrumb
            component="a"
            href="/"
            label="Dashboard"
            icon={<HomeIcon fontSize="small" />}
          />
          <StyledBreadcrumb
            component="a"
            href="/homeFestiveBanners"
            label="Festive Banners"
          />
          <StyledBreadcrumb label="Active Offers" deleteIcon={<ExpandMoreIcon />} />
        </Breadcrumbs>
      </div>

      <Box sx={{ marginTop: '32px' }}>
        {loading ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            }}
          >
            <CircularProgress size={48} sx={{ marginBottom: '16px' }} />
            <Typography sx={{ fontSize: '1rem', color: '#757575', fontWeight: 500 }}>
              Loading active offers...
            </Typography>
          </Box>
        ) : activeOffers.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
              borderRadius: '12px',
              border: '2px dashed #e0e0e0',
            }}
          >
            <MdLocalOffer
              size={60}
              color="#bdbdbd"
              style={{
                marginBottom: '16px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                marginTop: '16px',
                marginBottom: '8px',
                fontWeight: 600,
                color: '#616161',
              }}
            >
              No active offers
            </Typography>
            <Typography
              sx={{
                color: '#9e9e9e',
                fontSize: '0.95rem',
              }}
            >
              All your active offers will appear here
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {activeOffers.map((offer) => {
              const firstMedia = offer.images?.[0];
              const isVideo = isVideoUrl(firstMedia);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={offer._id}>
                  <Card
                    className="active-offer-card"
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      height: '100%',
                      maxHeight: '480px',
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-6px) scale(1.02)',
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                        borderColor: '#4caf50',
                        '& .offer-media': {
                          transform: 'scale(1.08)',
                        },
                      },
                    }}
                  >
                    {/* Offer Media - Image or Video */}
                    <Box sx={{ position: 'relative', height: 140, overflow: 'hidden' }}>
                      {isVideo ? (
                        <>
                          <video
                            src={firstMedia}
                            className="offer-media"
                            style={{
                              width: '100%',
                              height: '140px',
                              objectFit: 'cover',
                              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '50%',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}
                          >
                            <BiPlay size={28} color="#ffffff" />
                          </Box>
                        </>
                      ) : (
                        <CardMedia
                          component="img"
                          height="140"
                          image={firstMedia || "/placeholder.jpg"}
                          alt={offer.title}
                          className="offer-media"
                          sx={{
                            objectFit: "cover",
                            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      )}

                      {/* Media Count Badge */}
                      {offer.images?.length > 1 && (
                        <Chip
                          label={`${offer.images.length} files`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(10px)',
                            color: '#ffffff',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            height: '20px',
                          }}
                        />
                      )}

                      {/* Video Badge */}
                      {isVideo && (
                        <Chip
                          label="VIDEO"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(156, 39, 176, 0.9)',
                            backdropFilter: 'blur(10px)',
                            color: '#ffffff',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            height: '20px',
                          }}
                        />
                      )}
                    </Box>

                    {/* Card Content */}
                    <CardContent
                      sx={{
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {/* Title and Active Badge */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          className="offer-title"
                          sx={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: '#263238',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: '2.6em',
                            flex: 1,
                          }}
                        >
                          {offer.title}
                        </Typography>
                        <Chip
                          icon={<MdLocalOffer size={14} />}
                          label="Active"
                          color="success"
                          size="small"
                          sx={{
                            height: '22px',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        />
                      </Box>

                      {/* Description */}
                      {offer.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#757575',
                            fontSize: '0.8rem',
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            marginBottom: '8px',
                            lineHeight: 1.4,
                          }}
                        >
                          {offer.description}
                        </Typography>
                      )}

                      {/* Brand and Discount Chips */}
                      <Box
                        sx={{
                          marginBottom: '8px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        {offer.brand && (
                          <Chip
                            label={offer.brand}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {offer.discount > 0 && (
                          <Chip
                            label={`${offer.discount}% OFF`}
                            color="error"
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Box>

                      {/* Time Remaining */}
                      <Box sx={{ marginBottom: '8px' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#757575',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            marginBottom: '6px',
                          }}
                        >
                          <MdSchedule size={14} />
                          {calculateDaysRemaining(offer.endDate)}
                        </Typography>

                        {/* Progress Bar */}
                        {offer.endDate && (
                          <LinearProgress
                            variant="determinate"
                            value={getTimeProgress(offer.startDate, offer.endDate)}
                            color={
                              getTimeProgress(offer.startDate, offer.endDate) > 80
                                ? "error"
                                : "primary"
                            }
                            sx={{
                              height: '6px',
                              borderRadius: '3px',
                              backgroundColor: '#e0e0e0',
                            }}
                          />
                        )}
                      </Box>

                      {/* Products Count and View Button */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 'auto',
                          paddingTop: '8px',
                          borderTop: '1px solid #f5f5f5',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#757575',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          {offer.products?.length || 0} Products
                        </Typography>

                        <Tooltip title="View Details" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(`/homeFestiveBanners/details/${offer._id}`)
                            }
                            sx={{
                              padding: '6px',
                              background: 'rgba(25, 118, 210, 0.08)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: '#1976d2',
                                color: '#ffffff',
                                transform: 'scale(1.15)',
                              },
                            }}
                          >
                            <FaEye size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Dates */}
                      <Box
                        sx={{
                          marginTop: '8px',
                          padding: '8px',
                          background: '#f5f5f5',
                          borderRadius: '6px',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#616161',
                            fontSize: '0.7rem',
                            display: 'block',
                            lineHeight: 1.5,
                          }}
                        >
                          Started: {formatDate(offer.startDate)}
                        </Typography>
                        {offer.endDate && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#616161',
                              fontSize: '0.7rem',
                              display: 'block',
                              lineHeight: 1.5,
                            }}
                          >
                            Ends: {formatDate(offer.endDate)}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </div>
  );
};

export default ActiveFestiveOffers;
